'use client';

/**
 * @fileoverview Cloudflare Turnstile CAPTCHA component.
 * Provides bot detection and human verification without intrusive image puzzles.
 * Uses official Cloudflare Turnstile API with automatic script loading and cleanup.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';

// Production Cloudflare Turnstile Site Key for postbrix.com
const DEFAULT_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '0x4AAAAAAEgZptqsF28H4QC1';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        params: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode?: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact';
          action?: string;
          cData?: string;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onTurnstileLoaded?: () => void;
  }
}

export interface CloudflareTurnstileProps {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: (errorCode?: string) => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
  action?: string;
  className?: string;
}

export function CloudflareTurnstile({
  onVerify,
  onExpire,
  onError,
  theme = 'auto',
  size = 'normal',
  action,
  className = '',
}: CloudflareTurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  // Load the Turnstile script dynamically
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (window.turnstile) {
      setScriptLoaded(true);
      return;
    }

    const scriptId = 'cf-turnstile-script';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => setScriptLoaded(true);
      script.onerror = () => {
        if (onError) onError('SCRIPT_LOAD_FAILED');
      };
      document.head.appendChild(script);
    } else {
      script.addEventListener('load', () => setScriptLoaded(true));
    }
  }, [onError]);

  // Render the widget when script is ready and container is mounted
  const renderWidget = useCallback(() => {
    if (!window.turnstile || !containerRef.current) return;

    // Clean up any existing widget before re-rendering
    if (widgetIdRef.current) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch {
        // Ignore removal error
      }
      widgetIdRef.current = null;
    }

    try {
      const id = window.turnstile.render(containerRef.current, {
        sitekey: DEFAULT_SITE_KEY,
        theme,
        size,
        action,
        callback: (token: string) => {
          if (onVerify) onVerify(token);
        },
        'expired-callback': () => {
          if (onExpire) onExpire();
        },
        'error-callback': (errorCode?: string) => {
          if (onError) onError(errorCode);
        },
      });
      widgetIdRef.current = id;
    } catch (err) {
      console.warn('[CloudflareTurnstile] Render error:', err);
    }
  }, [theme, size, action, onVerify, onExpire, onError]);

  useEffect(() => {
    if (scriptLoaded) {
      renderWidget();
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore removal error
        }
        widgetIdRef.current = null;
      }
    };
  }, [scriptLoaded, renderWidget]);

  return (
    <div
      ref={containerRef}
      className={`cf-turnstile-container my-2 flex items-center justify-start min-h-[65px] ${className}`}
      data-testid="cf-turnstile"
    />
  );
}
